import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Send, Copy, Inbox, X, FileSpreadsheet, BellRing, ShieldCheck, ClipboardList } from "lucide-react";
import { AddCollectorDialog } from "@/components/company/AddCollectorDialog";
import { fileUrl } from "@/lib/files";
import { BulkImportDialog } from "@/components/company/BulkImportDialog";
import { labelFor, normalizeServiceCode } from "@/lib/services";

export const collectorName = (c) => [c.first_name, c.last_name].filter(Boolean).join(" ") || c.business_name || "Collector";

const Chip = ({ cls, children, testid }) => <span data-testid={testid} className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{children}</span>;

export const CollectorChips = ({ c }) => (
  <div className="flex flex-wrap gap-1.5">
    {c.has_login ? <Chip cls="bg-[#2D6A4F]/15 text-[#2D6A4F]" testid={`collector-login-${c.id}`}>Login active</Chip>
      : c.invite?.status === "declined" ? <Chip cls="bg-[#E63946]/12 text-[#E63946]" testid={`collector-login-${c.id}`}>Invite declined</Chip>
      : <Chip cls="bg-[#E9C46A]/30 text-[#8A5A00]" testid={`collector-login-${c.id}`}>Invite pending</Chip>}
    {c.profile_completion && !c.profile_completion.complete
      ? <Chip cls="bg-[#E9C46A]/30 text-[#8A5A00]" testid={`collector-sub-${c.id}`}>Profile incomplete · {c.profile_completion.done}/{c.profile_completion.total} — no leads yet</Chip>
      : <Chip cls={c.subscription_active ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#1E3F33]/10 text-[#5C6B64]"} testid={`collector-sub-${c.id}`}>{c.subscription_active ? "Receiving leads" : "Not receiving leads"}</Chip>}
  </div>
);

export const useCollectorActions = (reload) => {
  const resend = async (c) => {
    try {
      const { data } = await api.post(`/company/collectors/${c.id}/resend-invite`);
      navigator.clipboard?.writeText(data.invite_link);
      toast.success("Invite resent — link copied to clipboard");
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const nudge = async (c) => {
    try {
      const { data } = await api.post(`/company/collectors/${c.id}/nudge`);
      toast.success(`Reminder sent by ${data.channels.join(" + ")}`);
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (c, after) => {
    const msg = c.has_login
      ? `Remove ${collectorName(c)} from your company? They keep their own login and become an independent collector.`
      : `Remove ${collectorName(c)}? Their profile will be deleted and any open leads released to other collectors.`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/company/collectors/${c.id}`); toast.success("Collector removed — your bill was updated"); await (after || reload)(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return { resend, remove, nudge };
};

export const NudgeInfo = ({ c }) => {
  const n = c.profile_nudges || {};
  return (
    <div className="text-[11px] text-[#8A9A86]" data-testid={`company-collector-nudge-info-${c.id}`}>
      {n.last_at ? `Reminded ${new Date(n.last_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "No reminder yet"} · auto reminders {n.count || 0}/6
    </div>
  );
};

const CollectorServices = ({ c }) => {
  const svcs = [...new Set((c.services || []).map(normalizeServiceCode))];
  if (!svcs.length) return <span className="text-[11px] text-[#8A5A00] inline-flex items-center gap-1" data-testid={`company-collector-services-${c.id}`}><ClipboardList className="w-3 h-3"/> No services yet</span>;
  return (
    <span className="flex flex-wrap gap-1" data-testid={`company-collector-services-${c.id}`}>
      {svcs.slice(0, 3).map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0EFEA] text-[#1E3F33]">{labelFor(s)}</span>)}
      {svcs.length > 3 && <span className="text-[10px] px-2 py-0.5 text-[#8A9A86]" title={svcs.slice(3).map(labelFor).join(", ")}>+{svcs.length - 3} more</span>}
    </span>
  );
};

const CertStatus = ({ c }) => {
  const certs = c.certifications || [];
  const verified = certs.filter((x) => x.verification_status === "verified").length;
  const cls = !certs.length ? "text-[#8A5A00]" : verified ? "text-[#2D6A4F]" : "text-[#5C6B64]";
  return (
    <span className={`text-[11px] inline-flex items-center gap-1 ${cls}`} data-testid={`company-collector-certs-${c.id}`}>
      <ShieldCheck className="w-3 h-3"/> {!certs.length ? "No certifications" : `${certs.length} cert${certs.length === 1 ? "" : "s"}${verified ? ` · ${verified} verified` : " · pending review"}`}
    </span>
  );
};

const CollectorRow = ({ c, actions }) => {
  const navigate = useNavigate();
  const photo = c.photo_url ? (c.photo_url.startsWith("http") ? c.photo_url : `${API}/files/${c.photo_url}`) : null;
  const logo = !photo && c.company_logo_url ? fileUrl(c.company_logo_url) : null;
  return (
    <li className="py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid={`company-collector-${c.id}`}>
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <div className="w-11 h-11 rounded-full bg-[#2D6A4F]/10 overflow-hidden flex items-center justify-center font-heading text-[#1E3F33] shrink-0">
          {photo ? <img src={photo} alt="" className="w-full h-full object-cover"/> : logo ? <img src={logo} alt="" className="w-full h-full object-contain p-1 bg-white"/> : collectorName(c).charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[#1E3F33] truncate" data-testid={`company-collector-name-${c.id}`}>{collectorName(c)}</div>
          <div className="text-xs text-[#8A9A86] truncate">{[c.phone, c.email].filter(Boolean).join(" · ") || "No contact info"}</div>
          {!c.profile_completion?.complete && <NudgeInfo c={c} />}
          <div className="mt-1.5"><CollectorChips c={c} /></div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5"><CollectorServices c={c} /><CertStatus c={c} /></div>
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-4">
      <div className="flex items-center gap-4 text-xs text-[#5C6B64]">
        <span className="inline-flex items-center gap-1" title="New leads" data-testid={`company-collector-open-leads-${c.id}`}><Inbox className="w-3.5 h-3.5"/> {c.open_leads}</span>
      </div>
      <div className="flex items-center gap-1.5">
        {c.invite?.link && <button onClick={() => { navigator.clipboard?.writeText(c.invite.link); toast.success("Invite link copied"); }} title="Copy invite link" data-testid={`company-collector-copy-${c.id}`} className="p-2 rounded-lg text-[#5C6B64] hover:bg-[#1E3F33]/5"><Copy className="w-4 h-4"/></button>}
        {!c.profile_completion?.complete && <button onClick={() => actions.nudge(c)} title="Send profile reminder" data-testid={`company-collector-nudge-${c.id}`} className="p-2 rounded-lg text-[#5C6B64] hover:bg-[#1E3F33]/5"><BellRing className="w-4 h-4"/></button>}
        {!c.has_login && <button onClick={() => actions.resend(c)} title="Resend invite" data-testid={`company-collector-resend-${c.id}`} className="p-2 rounded-lg text-[#5C6B64] hover:bg-[#1E3F33]/5"><Send className="w-4 h-4"/></button>}
        <button onClick={() => actions.remove(c)} title="Remove collector" data-testid={`company-collector-remove-${c.id}`} className="p-2 rounded-lg text-[#8A9A86] hover:text-[#E63946] hover:bg-[#E63946]/5"><Trash2 className="w-4 h-4"/></button>
        {!c.profile_completion?.complete && <button onClick={() => navigate(`/dashboard/company/collectors/${c.id}?tab=profile`)} data-testid={`company-collector-complete-${c.id}`} className="btn-primary text-xs py-1.5 px-3 whitespace-nowrap">Complete profile</button>}
        <button onClick={() => navigate(`/dashboard/company/collectors/${c.id}`)} data-testid={`company-collector-manage-${c.id}`} className="btn-secondary text-xs py-1.5 px-3">Manage</button>
      </div>
      </div>
    </li>
  );
};

const PendingJoinInvites = ({ invites, reload }) => {
  if (!invites.length) return null;
  const cancel = async (id) => {
    try { await api.delete(`/company/invites/${id}`); toast.success("Invite cancelled"); await reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="mt-6 pt-5 border-t border-[#1E3F33]/8" data-testid="company-join-invites">
      <div className="text-xs uppercase tracking-wider text-[#8A9A86] mb-2">Invites to existing collectors</div>
      <ul className="space-y-2">
        {invites.map((i) => (
          <li key={i.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`company-join-invite-${i.id}`}>
            <span className="text-[#1E3F33] truncate">{i.name} <span className="text-xs text-[#8A9A86]">· {i.phone || i.email}</span></span>
            <span className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${i.status === "declined" ? "bg-[#E63946]/12 text-[#E63946]" : "bg-[#E9C46A]/30 text-[#8A5A00]"}`}>{i.status === "declined" ? "Declined" : "Awaiting acceptance"}</span>
              <button onClick={() => cancel(i.id)} title="Cancel invite" data-testid={`company-join-invite-cancel-${i.id}`} className="p-1 text-[#8A9A86] hover:text-[#E63946]"><X className="w-4 h-4"/></button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const CompanyTeamCard = ({ collectors, invites, reload }) => {
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const actions = useCollectorActions(reload);
  return (
    <div className="card-soft" data-testid="company-team-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-xl text-[#1E3F33] flex items-center gap-2"><Users className="w-4 h-4 text-[#CC5A3A]"/> Your collectors <span className="text-sm text-[#8A9A86] font-normal">({collectors.length})</span></h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setImportOpen(true)} data-testid="import-collectors-button" className="btn-secondary text-sm inline-flex items-center gap-2"><FileSpreadsheet className="w-4 h-4"/> Import spreadsheet</button>
          <button onClick={() => setOpen(true)} data-testid="add-collector-button" className="btn-primary text-sm inline-flex items-center gap-2"><UserPlus className="w-4 h-4"/> Add collector</button>
        </div>
      </div>
      {collectors.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-[#1E3F33]/15 p-8 text-center" data-testid="company-team-empty">
          <Users className="w-8 h-8 text-[#8A9A86] mx-auto"/>
          <p className="text-sm text-[#5C6B64] mt-3">No collectors yet. Add your first collector to start building your team.</p>
        </div>
      ) : (
        <ul className="divide-y divide-[#1E3F33]/8 mt-2" data-testid="company-collectors-list">
          {collectors.map((c) => <CollectorRow key={c.id} c={c} actions={actions} />)}
        </ul>
      )}
      <PendingJoinInvites invites={invites} reload={reload} />
      <AddCollectorDialog open={open} onOpenChange={setOpen} onAdded={reload} />
      <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={reload} />
    </div>
  );
};
